import { useEffect, useState } from 'react';
import { Button, Grid, AlertColor } from '@mui/material';
import _ from 'lodash';
import { useFormik } from 'formik';
import { IMultiSelectControlField2, ISelectOption } from 'src/lib';
import {
  SelectComponent,
  AlertDialog,
  DialogResult,
} from 'src/components/analytics';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import MODULE_TO_PAGE_MAP from '../../../../home/types/simulation-module-to-page-map';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks';
import './index.scss';
import { IDynamicFilterFormFields } from '../../../../home/types/scenario-form.model';
import {
  getDuplicateSkuPopupFilters,
  getDuplicateSkuTable,
} from '../../../store/innovation-slice-async-thunks';
import {
  updateFilterFieldOptions,
  resetDuplicateSku,
} from '../../../store/innovation-slice';
import getInnovationPayload from '../../../utils/filter-utils';
import SnackbarComponent from '../../snackbar/snackbar';

export default function DuplicateFilters() {
  // app dispatch for dispatching actions
  const appDispatch = useAppDispatch();
  // Getting state from innovation
  const {
    appliedFilters,
    topFiltersValue,
    topFiltersDefaultValue,
    fetchingDuplicateSkuPopupFilters,
  } = useAppSelector(state => state.innovation);

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success',
  });

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION,
  });

  const {
    filters: filterFields,
    initialValues: filterInitialValues,
    combinations,
  } = topFiltersValue;

  // filter formik
  const filterFormik = useFormik<IDynamicFilterFormFields>({
    initialValues: filterInitialValues,
    enableReinitialize: true,
    onSubmit(values) {
      console.log(values);
    },
  });

  useEffect(() => {
    appDispatch(
      getDuplicateSkuPopupFilters({
        sourceId,
        moduleId,
        scenarioId: Number(scenarioId),
        filters: appliedFilters,
      })
    )
      .then(res => {
        if (res.meta.requestStatus === 'rejected') {
          setSnackbarContent({
            // eslint-disable-next-line dot-notation
            snackbarText: res['error'].message,
            snackbarType: 'error',
          });
          setSnackbar(true);
        }
      })
      .then(() =>
        appDispatch(
          getDuplicateSkuTable({
            sourceId,
            moduleId,
            scenarioId: Number(scenarioId),
            filters: appliedFilters,
            duplicateFilters: getInnovationPayload(
              filterInitialValues,
              topFiltersDefaultValue
            ),
          })
        ).then(res => {
          if (res.meta.requestStatus === 'rejected') {
            setSnackbarContent({
              // eslint-disable-next-line dot-notation
              snackbarText: res['error'].message,
              snackbarType: 'error',
            });
            setSnackbar(true);
          }
        })
      );
  }, []);

  //  function to set filter value on change
  const handleFilterSelectionChange = (
    fieldName: string,
    fieldValues: { label: string; value: string }[]
  ) => {
    const changedFilterIndex = filterFields.findIndex(
      filter => filter.name === fieldName
    );
    const lastItemIndex = filterFields.length - 1;
    if (changedFilterIndex > -1 && changedFilterIndex !== lastItemIndex) {
      const precedingFilterValues = {
        ...filterFields
          .slice(0, changedFilterIndex)
          .reduce<{ [filterName: string]: { label: string; value: string }[] }>(
            (previousValue, currentValue) => ({
              ...previousValue,
              ...{
                [currentValue.name]: filterFormik.values[currentValue.name],
              },
            }),
            {}
          ),
        ...{
          [fieldName]: fieldValues,
        },
      };
      precedingFilterValues[fieldName] = fieldValues;
      const newCombinations = combinations.filter(filterCombination => {
        const keys = Object.keys(precedingFilterValues);
        return (
          keys.filter(key =>
            precedingFilterValues[key]
              .map(val => val.value)
              .includes(filterCombination[key] as string)
          ).length === keys.length
        );
      });
      const remainingFilterValues: {
        [filterName: string]: { label: string; value: string }[];
      } = {};
      for (
        let index = changedFilterIndex + 1;
        index < filterFields.length;
        index++
      ) {
        const filterField = filterFields[index] as IMultiSelectControlField2;
        const dropdownOptionsForFilter = newCombinations
          .map(filterCombination => filterCombination[filterField.name])
          .filter(value => value)
          .filter(
            (value, valueIndex, self) => self.indexOf(value) === valueIndex
          )
          .map(val => ({ label: val as string, value: val as string }));
        // filterField.values = dropdownOptionsForFilter.slice();
        remainingFilterValues[filterField.name] =
          dropdownOptionsForFilter.slice();
      }
      filterFormik.setValues({
        ...precedingFilterValues,
        ...remainingFilterValues,
      });
      appDispatch(updateFilterFieldOptions(remainingFilterValues));
    }
    if (lastItemIndex === changedFilterIndex) {
      const previousValues = filterFormik.values;
      filterFormik.setValues({ ...previousValues, [fieldName]: fieldValues });
    }
  };

  /**
   * Handler for filter apply
   */
  const handleFilterApply = () => {
    // resetting the duplicate skus
    appDispatch(resetDuplicateSku());

    appDispatch(
      getDuplicateSkuTable({
        sourceId,
        moduleId,
        scenarioId: Number(scenarioId),
        filters: appliedFilters,
        duplicateFilters: getInnovationPayload(
          filterFormik.values,
          topFiltersDefaultValue
        ),
      })
    );
  };

  /**
   * Handler for filter reset button
   */
  const handleFilterReset = () => {
    // Reset values on filters to ALL

    // preparing default filter values for reset
    const defaultFilterValues = filterFields.reduce<{
      [key: string]: ISelectOption[];
    }>(
      (acc, curr) => ({
        ...acc,
        [curr.name]: curr.default_value as ISelectOption[],
      }),
      {}
    );
    // reseting the filter values in slice
    appDispatch(updateFilterFieldOptions(defaultFilterValues));

    // resetting the formik
    filterFormik.setValues(filterInitialValues);

    // resetting the duplicate skus
    appDispatch(resetDuplicateSku());

    /**
     * fetching table data with default value after reset
     * */
    appDispatch(
      getDuplicateSkuTable({
        sourceId,
        moduleId,
        scenarioId: Number(scenarioId),
        filters: appliedFilters,
        duplicateFilters: getInnovationPayload(
          filterInitialValues,
          topFiltersDefaultValue
        ),
      })
    );
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      handleFilterReset();
    }
    setConfirmationDialogOpen(false);
  };

  const onResetClick = () => {
    setConfirmationDialogOpen(true);
  };

  return (
    <>
      {Object.keys(filterFormik.values || {}).length > 0 &&
      !fetchingDuplicateSkuPopupFilters ? (
        <Grid className='filter-container' container alignItems='center'>
          <Grid item container spacing={1} xs justifyContent='stretch'>
            {filterFields.map((control: IMultiSelectControlField2) => (
              <Grid item xs={12 / filterFields.length} key={control.name}>
                <SelectComponent
                  value={filterFormik.values[control.name]}
                  handleChange={val =>
                    handleFilterSelectionChange(control.name, val)
                  }
                  isDisabled={false}
                  label={control.alias}
                  options={control.values}
                  isMultiSelect
                />
              </Grid>
            ))}
          </Grid>
          <Grid
            pl={1}
            pt={2}
            item
            xs='auto'
            container
            spacing={1}
            justifyContent='center'
          >
            <Grid item xs>
              <Button
                // disabled={
                //   !props.isValid || disableSubmit || !isApplyEnabled
                // }
                onClick={handleFilterApply}
                type='submit'
                variant='outlined'
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs>
              <Button
                onClick={onResetClick}
                // disabled={disableReset}
                type='button'
                variant='outlined'
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <>{FilterLoadingSkeleton(4)}</>
      )}
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message='Filters will reset and all changes to simulation will be lost. Continue?'
        title='Reset'
      />
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </>
  );
}
